import React from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MultiSelect, MultiSelectOption } from '../../components/shared/MultiSelect';
import { ScrollArea } from '../../components/ui/scroll-area';
import { LicenseListFilters } from '../../services/licenseService';

// Filter fields the drawer edits — everything in LicenseListFilters except the
// ones the listing page already owns directly (free-text search, status tab,
// sort, pagination).
export type LicenseAdvancedFilterState = Omit<
    LicenseListFilters,
    'search' | 'tab' | 'sortBy' | 'sortDir' | 'page' | 'limit'
>;

export const EMPTY_LICENSE_FILTERS: LicenseAdvancedFilterState = {};

/** Number of advanced filters currently applied — drives the "Filters (3)" badge. */
export function countActiveLicenseFilters(filters: LicenseAdvancedFilterState): number {
    return Object.values(filters).filter((v) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== ''
    ).length;
}

interface LicenseAdvancedFiltersProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** In-progress edits, owned by the page's table-state hook. */
    draft: LicenseAdvancedFilterState;
    onDraftChange: (filters: LicenseAdvancedFilterState) => void;
    onApply: () => void;
    onClear: () => void;
    categoryOptions: MultiSelectOption[];
    typeOptions: MultiSelectOption[];
    vendorOptions: MultiSelectOption[];
    billingFrequencyOptions: MultiSelectOption[];
    commitmentTermOptions: MultiSelectOption[];
    renewalStatusOptions: MultiSelectOption[];
    complianceRiskOptions: MultiSelectOption[];
    userOptions: MultiSelectOption[];
    licensePlanOptions: MultiSelectOption[];
    departmentOptions: MultiSelectOption[];
}

export const LicenseAdvancedFilters: React.FC<LicenseAdvancedFiltersProps> = ({
    open,
    onOpenChange,
    draft,
    onDraftChange,
    onApply,
    onClear,
    categoryOptions,
    typeOptions,
    vendorOptions,
    billingFrequencyOptions,
    commitmentTermOptions,
    renewalStatusOptions,
    complianceRiskOptions,
    userOptions,
    licensePlanOptions,
    departmentOptions,
}) => {
    // Local draft — only committed to the page (and the URL) on "Apply Filters".
    // The draft lives in the page's table-state hook, which resets it to the
    // applied filters whenever the drawer opens — so editing here never
    // mutates the list until "Apply Filters" commits it.
    const set = <K extends keyof LicenseAdvancedFilterState>(key: K, value: LicenseAdvancedFilterState[K]) => {
        onDraftChange({ ...draft, [key]: value });
    };

    const idsToStrings = (ids?: number[]) => (ids ?? []).map(String);
    const stringsToIds = (values: string[]) => (values.length ? values.map(Number) : undefined);

    const handleApply = () => {
        onApply();
        onOpenChange(false);
    };

    // onClear resets both the applied filters and the draft in one go.
    const handleClearAll = () => {
        onClear();
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="sm:max-w-lg">
                <SheetHeader>
                    <SheetTitle>Advanced Filters</SheetTitle>
                </SheetHeader>

                <ScrollArea className="flex-1 min-h-0">
                    <div className="space-y-6 px-6 py-5">
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold">Classification</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-plan">License Plan</Label>
                                <MultiSelect
                                    id="lfilter-plan"
                                    options={licensePlanOptions}
                                    selected={idsToStrings(draft.licensePlanId)}
                                    onChange={(v) => set('licensePlanId', stringsToIds(v))}
                                    placeholder="Any plan"
                                    searchPlaceholder="Search plans…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-category">Category</Label>
                                <MultiSelect
                                    id="lfilter-category"
                                    options={categoryOptions}
                                    selected={draft.category ?? []}
                                    onChange={(v) => set('category', v.length ? v : undefined)}
                                    placeholder="Any category"
                                    searchPlaceholder="Search categories…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-type">Licensing Model</Label>
                                <MultiSelect
                                    id="lfilter-type"
                                    options={typeOptions}
                                    selected={draft.type ?? []}
                                    onChange={(v) => set('type', v.length ? v : undefined)}
                                    placeholder="Any licensing model"
                                    searchPlaceholder="Search models…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-vendor">Vendor</Label>
                                <MultiSelect
                                    id="lfilter-vendor"
                                    options={vendorOptions}
                                    selected={draft.vendor ?? []}
                                    onChange={(v) => set('vendor', v.length ? v : undefined)}
                                    placeholder="Any vendor"
                                    searchPlaceholder="Search vendors…"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Billing</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-billing">Billing Frequency</Label>
                                <MultiSelect
                                    id="lfilter-billing"
                                    options={billingFrequencyOptions}
                                    selected={draft.billingFrequency ?? []}
                                    onChange={(v) => set('billingFrequency', v.length ? v : undefined)}
                                    placeholder="Any frequency"
                                    searchPlaceholder="Search frequencies…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-commitment">Commitment Term</Label>
                                <MultiSelect
                                    id="lfilter-commitment"
                                    options={commitmentTermOptions}
                                    selected={draft.commitmentTerm ?? []}
                                    onChange={(v) => set('commitmentTerm', v.length ? v : undefined)}
                                    placeholder="Any term"
                                    searchPlaceholder="Search terms…"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Status &amp; Risk</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-renewal">Renewal Status</Label>
                                <MultiSelect
                                    id="lfilter-renewal"
                                    options={renewalStatusOptions}
                                    selected={draft.renewalStatus ?? []}
                                    onChange={(v) => set('renewalStatus', v.length ? v : undefined)}
                                    placeholder="Any renewal status"
                                    searchPlaceholder="Search statuses…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-risk">Compliance Risk</Label>
                                <MultiSelect
                                    id="lfilter-risk"
                                    options={complianceRiskOptions}
                                    selected={draft.complianceRisk ?? []}
                                    onChange={(v) => set('complianceRisk', v.length ? v : undefined)}
                                    placeholder="Any risk level"
                                    searchPlaceholder="Search risk levels…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-assignedTo">Assigned To</Label>
                                <MultiSelect
                                    id="lfilter-assignedTo"
                                    options={userOptions}
                                    selected={idsToStrings(draft.assignedToId)}
                                    onChange={(v) => set('assignedToId', stringsToIds(v))}
                                    placeholder="Any user"
                                    searchPlaceholder="Search users…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lfilter-department">Department</Label>
                                <MultiSelect
                                    id="lfilter-department"
                                    options={departmentOptions}
                                    selected={idsToStrings(draft.departmentId)}
                                    onChange={(v) => set('departmentId', stringsToIds(v))}
                                    placeholder="Any department"
                                    searchPlaceholder="Search departments…"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Dates</h4>
                            <div className="space-y-1.5">
                                <Label>Purchase Date Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="date"
                                        aria-label="Purchase date from"
                                        value={draft.purchaseDateFrom ?? ''}
                                        onChange={(e) => set('purchaseDateFrom', e.target.value || undefined)}
                                    />
                                    <Input
                                        type="date"
                                        aria-label="Purchase date to"
                                        value={draft.purchaseDateTo ?? ''}
                                        onChange={(e) => set('purchaseDateTo', e.target.value || undefined)}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Expiry Date Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="date"
                                        aria-label="Expiry date from"
                                        value={draft.expiryDateFrom ?? ''}
                                        onChange={(e) => set('expiryDateFrom', e.target.value || undefined)}
                                    />
                                    <Input
                                        type="date"
                                        aria-label="Expiry date to"
                                        value={draft.expiryDateTo ?? ''}
                                        onChange={(e) => set('expiryDateTo', e.target.value || undefined)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Financials</h4>
                            <div className="space-y-1.5">
                                <Label>Total Cost Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        aria-label="Total cost minimum"
                                        placeholder="Min"
                                        value={draft.totalCostMin ?? ''}
                                        onChange={(e) => set('totalCostMin', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        aria-label="Total cost maximum"
                                        placeholder="Max"
                                        value={draft.totalCostMax ?? ''}
                                        onChange={(e) => set('totalCostMax', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Total Seats Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="number"
                                        min={0}
                                        aria-label="Total seats minimum"
                                        placeholder="Min"
                                        value={draft.totalSeatsMin ?? ''}
                                        onChange={(e) => set('totalSeatsMin', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        aria-label="Total seats maximum"
                                        placeholder="Max"
                                        value={draft.totalSeatsMax ?? ''}
                                        onChange={(e) => set('totalSeatsMax', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Seat Utilization Range (%)</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="number"
                                        min={0}
                                        max={100}
                                        aria-label="Seat utilization minimum percent"
                                        placeholder="Min %"
                                        value={draft.utilizationMin ?? ''}
                                        onChange={(e) => set('utilizationMin', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        max={100}
                                        aria-label="Seat utilization maximum percent"
                                        placeholder="Max %"
                                        value={draft.utilizationMax ?? ''}
                                        onChange={(e) => set('utilizationMax', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </ScrollArea>

                <SheetFooter>
                    <Button type="button" variant="outline" onClick={handleClearAll}>
                        Clear All
                    </Button>
                    <Button type="button" onClick={handleApply}>
                        Apply Filters
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};
